import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Platform } from 'src/app/models/api';
import { PlatformIconPaths } from 'src/app/models/constants/platform.constants';
import { ISelectOption, PlatformSelectOptions } from 'src/app/models/selectOptions';
import { IPlaybookTemplateCard } from 'src/app/models/templates/playbooks/playbookTemplateCard.interface';
import { IPlaybookTemplateDetails } from 'src/app/models/templates/playbooks/playbookTemplateDetails.interface';
import { PlaybookTemplatesDetailsModule } from 'src/app/models/templates/playbooks/playbookTemplateDetails.module';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { PlaybookTemplatesService } from 'src/app/services/templates/playbook/playbook-templates.service';

@Component({
  selector: 'app-playbooks-marketplace',
  templateUrl: './playbooks-marketplace.component.html',
  styleUrls: ['./playbooks-marketplace.component.scss'],
})
export class PlaybooksMarketplaceComponent {
  public playbookTemplateCards: BehaviorSubject<IPlaybookTemplateCard[]> = new BehaviorSubject<IPlaybookTemplateCard[]>(
    []
  );
  public platformOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public selectedPlatformIds: string[] = [];
  private readonly _playbookTemplatesDetails: IPlaybookTemplateDetails[] = PlaybookTemplatesDetailsModule;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _playbookTemplatesService: PlaybookTemplatesService
  ) {
    this._store.integrations$.subscribe((integrations) => {
      const platforms = new Set(
        integrations
          .filter((integration) =>
            this._playbookTemplatesService.isIntegrationSupportedForPlaybookTemplates(integration)
          )
          .map((integration) => integration.platform)
      );
      this.platformOptions$.next(
        PlatformSelectOptions.filter((option) => platforms.has(option.id as Platform)).map(
          (option) =>
            ({
              id: option.id,
              value: option.value,
              displayName: option.displayName,
              iconPath: option.iconPath,
            }) as ISelectOption
        )
      );
      this.selectedPlatformIds = this.platformOptions$.value.map((option) => option.id);
      this.playbookTemplateCards.next(
        this._playbookTemplatesDetails
          .filter((template) => platforms.has(template.platform))
          .map((template) => this._mapPlaybookTemplateDetailsToCard(template))
      );
    });
  }

  public selectTemplate(templateId: string) {
    this._router.navigateByUrl(`/app/playbooks/new/templates/${templateId}`);
  }

  public selectPlatform(options: ISelectOption[]) {
    let platforms = options.map((option) => option.id as Platform);
    if (options.length == 0) {
      platforms = this.platformOptions$.value.map((option) => option.id as Platform);
    }
    this.selectedPlatformIds = platforms;
    this.playbookTemplateCards.next(
      this._playbookTemplatesDetails
        .filter((template) => new Set(platforms).has(template.platform))
        .map((template) => this._mapPlaybookTemplateDetailsToCard(template))
    );
  }

  private _mapPlaybookTemplateDetailsToCard(template: IPlaybookTemplateDetails): IPlaybookTemplateCard {
    return {
      titleLabel: template.titleLabel,
      descriptionLabel: template.subTitleLabel,
      logoPath: PlatformIconPaths[template.platform],
      actionLabel: 'playbooksMarketplace.selectTemplateActionDisplayName',
      actionData: template.key,
    };
  }
}
